import React, { useState, useRef, useEffect } from "react";
import {
  getPopupRegistration,
  openPopup,
  Priority,
} from "app/PopupHolder";
import CommonModal from "components/base/CommonModal";
import SVG from "components/base/SVG";
import css from "./style.scss";
import { validate, verify, verifyRequired } from "utils/validation";
import SimpleModal from "../../../components/base/SimpleModal";

import { selectTags } from 'data/property';
import { saveTag, deleteTag } from 'data/user';
import { useDispatch, useSelector } from "react-redux";

const MangePropertyTagModal = (props) => {
  const [search, setSearch] = useState("");
  const [newTag, setNewTag] = useState("");
  const [mode, setMode] = useState(null); // create | suggest | null
  const [showOptions, setShowOptions] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [editName, setEditName] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [createError, setCreateError] = useState("");
  const [editError, setEditError] = useState("");
  const tagsdetails = useSelector(selectTags);
  const dispatch = useDispatch();
  const tags = tagsdetails?.toJS() || [];

  const suggestedTags = [
    "Move-In Ready",
    "Fixer-Upper",
    "Recently Updated",
    "Needs Exterior Improvements",
    "Needs Interior Improvements",
  ];

  const showWhiteIcon = showOptions || isHovering;

  const dropdownRef = useRef(null);

  const filteredTags = (tags || []).filter(tag =>
    tag.name.toLowerCase().startsWith(search.trim().toLowerCase())
  );

  const hasNoTags = !tags || tags.length === 0;

  const normalizedTags = tags?.map(t => t.name.toLowerCase());
  const isDuplicate = newTag.trim() && normalizedTags.includes(newTag.trim().toLowerCase());
  const canSave = mode === "create" && newTag.trim().length > 0 && !isDuplicate;

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleCreateTag = () => {
    const n = newTag.trim();

    validate(() => {
      verifyRequired(null, n, "Name is required.");
      verify(null, n.length <= 22, "Name length cannot exceed 22 characters.");

      dispatch(saveTag(
        { name: n, type: "P" },
        () => {
          setNewTag("");
          setMode(null);
        }
      ));
    });
  };

  const isCreateMode = mode === "create";
  const isEditMode = !!editingTag;
  const isSaveDisabled = isCreateMode ? !canSave : isEditMode ? !isDirty || isEditInvalid : false;

  const handleSaveClick = () => {
    if (isEditMode) {
      if (!isDirty) {
        // no change → just close edit
        cancelEdit();
        return;
      }
      handleUpdateTag();
      return;
    }

    if (isCreateMode) {
      handleCreateTag();
      return;
    }

    // nothing happening → close modal
    props.closePopup();
  };

  const editValue = editName.trim();
  const isEditDuplicate = isEditMode && editValue && tags.some(t => t.id !== editingTag?.id && t.name.toLowerCase() === editValue.toLowerCase());
  const isEditInvalid = isEditMode && (!editValue || isEditDuplicate);

  const startEdit = (tag) => {
    setEditingTag(tag);
    setEditName(tag.name);
    setIsDirty(false);
    setEditError("");
  };

  const handleEditChange = (e) => {
    const value = e.target.value;
    const trimmed = value.trim();

    setEditName(value);
    setIsDirty(trimmed !== editingTag?.name);
    if (!trimmed) {
      setEditError("Please enter a tag name.");
      return;
    }

    if (
      tags.some(
        t =>
          t.id !== editingTag?.id &&
          t.name.toLowerCase() === trimmed.toLowerCase()
      )
    ) {
      setEditError("Tag name already exists. Try a different one.");
      return;
    }

    setEditError("");

  };

  const handleUpdateTag = () => {
    if (!editingTag || !isDirty) return;

    const n = editName.trim();

    validate(() => {
      verifyRequired(null, n, "Name is required.");
      verify(null, n.length <= 22, "Name length cannot exceed 22 characters.");

      const duplicate = tags.some(
        t => t.id !== editingTag.id && t.name.toLowerCase() === n.toLowerCase()
      );

      if (duplicate) {
        setEditError("Tag name already exists. Try a different one.");
        return;
      }

      dispatch(
        saveTag(
          { id: editingTag.id, name: n, type: "P" },
          () => {
            setEditingTag(null);
            setEditName("");
            setIsDirty(false);
          }
        )
      );
    });
  };

  const cancelEdit = () => {
    setEditingTag(null);
    setEditName("");
    setIsDirty(false);
    setEditError("");
  };


  const onDeleteClick = (tag) => {
    setTagToDelete(tag);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (!tagToDelete) return;
    // capture required data BEFORE clearing state
    const tagId = tagToDelete.id;
    // close confirm modal immediately
    setShowDeleteConfirm(false);
    setTagToDelete(null);
    // remove from UI ONLY after success
    dispatch(deleteTag(tagId));
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setTagToDelete(null);
  };

  const handleSuggestedTagClick = (tagName) => {
    const name = tagName.trim();
    // prevent duplicates
    if (
      tags.some(t => t.name.toLowerCase() === name.toLowerCase())
    ) {
      setCreateError("Tag name already exists. Try a different one.");
      return;
    }

    dispatch(
      saveTag(
        { name, type: "P" },
        () => {
          setMode(null);     // close suggest view
          setCreateError("");
        }
      )
    );
  };

  return (
    <CommonModal
      onClose={props.closePopup}
      className={css.modal}
      closeOnBackdrop={true}
    >
      <div className={css.modalTitle}>Property Tags</div>

      {/* Top Row */}
      <div className={css.topRow}>
        <div className={css.searchBox}>
          <SVG icon="searchicon" className={css.searchIcon} />
          <input
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className={css.addWrapper} ref={dropdownRef}>
          <div
            className={`${css.addBtnWrapper} ${showOptions ? css.active : ""}`}
            onClick={() => setShowOptions(!showOptions)}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className={css.addBtn}>Add Tag</div>
            <span className={css.plusCircle}>
              <SVG className={css.addIcon}
                icon={showWhiteIcon ? "whiteAddIcon" : "addIcon"}
              />
            </span>
          </div>

          {showOptions && (
            <div className={css.dropdown}>
              <div
                className={css.dropdownItem}
                onClick={() => {
                  setMode("create");
                  setShowOptions(false);
                  setCreateError("");
                }}
              >
                Create My Own
              </div>
              <div
                className={css.dropdownItem}
                onClick={() => {
                  setMode("suggest");
                  setShowOptions(false);
                  setCreateError("");
                }}
              >
                Suggest a Tag
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={css.divider} />

      {/* Create Input */}
      {mode === "create" && (
        <>
          <div className={css.tagRow}>
            <input
              className={`${css.tagName} ${createError ? css.inputError : ""}`}
              placeholder="Enter Tag Name"
              value={newTag}
              onChange={(e) => {
                const value = e.target.value;
                setNewTag(value);

                const trimmedValue = value.trim();

                if (!trimmedValue) {
                  setCreateError("Please add tag name.");
                } else if (
                  tags.some(
                    t => t.name.toLowerCase() === trimmedValue.toLowerCase()
                  )
                ) {
                  setCreateError("Tag name already exists. Try a different one.");
                } else {
                  setCreateError("");
                }

              }}
            />
            <div className={css.deleteWrapper}>
              <SVG
                icon="deleteicon"
                className={css.deleteIcon}
                onClick={() => {
                  setNewTag("");
                  setMode(null);
                }}
              />
            </div>
          </div>
          {createError && <div className={css.errorText}>{createError}</div>}
        </>
      )}

      {/* TAG SUGGESTIONS */}
      {mode === "suggest" && (
        <div className={css.tagSuggestionWrapper}>
          <div className={css.suggestionHeader}>
            <div className={css.suggestionTitle}>Tag Suggestions</div>
            <SVG
              icon="iconClose"
              className={css.closeIcon}
              onClick={() => setMode(null)}
            />
          </div>

          <div className={css.suggestionList}>
            {suggestedTags?.map(tagName => (
              <div
                key={tagName}
                className={css.suggestionChip}
                onClick={() => handleSuggestedTagClick(tagName)}
              >
                <SVG icon="sparkleIcon" className={css.sparkleIcon} />
                <div className={css.suggestionName}>{tagName}</div>
              </div>
            ))}
          </div>
          {createError && <div className={css.errorText}>{createError}</div>}
        </div>
      )}


      {hasNoTags && mode !== "suggest" && (
        <div className={css.tagSuggestionWrapper}>
          <div className={css.emptyTitleHeader}>
            <div className={css.emptyTitle}>You Don’t Have Any Tags Yet</div>
            <div className={css.emptySub}>Select one below to get started</div>
          </div>

          <div className={css.suggestionList}>
            {suggestedTags?.map(tagName => (
              <div
                key={tagName}
                className={css.suggestionChip}
                onClick={() => handleSuggestedTagClick(tagName)}
              >
                <SVG icon="sparkleIcon" className={css.sparkleIcon} />
                <div className={css.suggestionName}>{tagName}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasNoTags && (
        <div className={css.tagList} >
          {search.trim().length > 0 && !hasNoTags && filteredTags.length === 0 ? (
            <div className={css.noresult}>
              No matching results.
            </div>
          ) : (
            filteredTags.map(tag => {
              const isEditing = editingTag?.id === tag.id;
              return (
                <>
                  {isEditing ? (
                    <>
                      <div key={tag.id} className={css.tagRow}>
                        <input
                          className={`${css.tagName} ${editError ? css.inputError : ""}`}
                          value={editName}
                          maxLength={22}
                          autoFocus
                          onChange={handleEditChange}
                        />
                        <div
                          className={css.deleteWrapper}
                          onClick={cancelEdit}
                        >
                          <SVG icon="deleteicon" className={css.deleteIcon} />
                        </div>
                      </div>
                      {editError && <div className={css.errorText}>{editError}</div>}
                    </>
                  ) : (
                    <div key={tag.id} className={css.tagRow}>
                      <div
                        className={css.tagName}
                        onClick={() => startEdit(tag)}
                      >
                        {tag.name}
                      </div>
                      <div
                        className={css.deleteWrapper}
                        onClick={() => onDeleteClick(tag)}
                      >
                        <SVG icon="deleteicon" className={css.deleteIcon} />
                      </div>
                    </div>
                  )}
                </>
              );
            }))}
        </div>
      )}

      {/* Footer */}
      <div className={css.footer}>
        <button className={css.btnSecondary} onClick={() => { cancelEdit(); setCreateError(""); props.closePopup(); }}>Cancel</button>

        <button className={css.btnPrimary} disabled={isSaveDisabled} onClick={handleSaveClick}>Save</button>
      </div>

      {showDeleteConfirm && (
        <SimpleModal
          title="Remove Property Tag?"
          description="This tag will be removed from all properties where it’s currently applied."
          onCancel={cancelDelete}
          modalClassName={css.deleteConfirmModal}
          customFooter={
            <div className={css.customFooter}>
              <button className={css.cancelSecondary} onClick={cancelDelete}>Cancel</button>
              <button className={`${css.dangerBtn}`} onClick={confirmDelete}>Remove</button>
            </div>
          }
        />
      )}

    </CommonModal>
  );
};

/* Popup registration */
const registrationId = getPopupRegistration(MangePropertyTagModal);

MangePropertyTagModal.open = (props = {}) =>
  openPopup(registrationId, {
    ...props,
    priority: Priority.HIGH,
    closeOnBackdrop: true,
    closeAll: false,
  });

export default MangePropertyTagModal;
